'use client'

import React, { ReactNode, useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { z } from 'zod'

import { loginUserState } from '@/components/Context/JwtTokenProvider'
import '@/components/Form/Organisms/SurveyForm/SurveyForm.scss'
import useSurveyAnswer from '@/components/hooks/useSurveyAnswer'
import { useZodResolver } from '@/components/hooks/useZodResolver'
import { SurveyAnswer, answerSurvey } from '@/serverActions/answerSurvey'
import { getLoginSession } from '@/serverActions/getLoginSession'
import { SurveyType } from '@/types/survey'
import FormResolverUtils from '@/utils/FormResolverUtils'
import { InputKey } from '@/utils/inputKey'

export type Props = {
  survey: SurveyType
  FormComponent: (_: SurveyFormComponentType) => ReactNode
  afterSubmit: () => void
  simple?: boolean
  lineIdToken?: string | null
  liffId?: string | null
}

export type SurveyFormComponentType = {
  form: any
  handleSubmit: (_: any) => void
  survey: SurveyType
  simple: boolean
}

export default function Form({ survey, afterSubmit, FormComponent, lineIdToken, liffId, simple = false }: Props) {
  const { zodDynamicResolver } = useZodResolver()
  const setLoginUser = useSetRecoilState(loginUserState)
  const surveyAnswersKeyName = 'surveyAnswers'

  const { setSurveyAnswer } = useSurveyAnswer()
  const loginUser = useRecoilValue(loginUserState)

  const form = useForm({
    mode: 'onChange',
    resolver: zodDynamicResolver(
      z.object({
        [surveyAnswersKeyName]: z.object({
          ...survey.surveyQuestions.reduce((previousValue, surveyQuestion) => {
            const formResolverUtils = FormResolverUtils({
              schema: z.array(z.string()).or(z.string()),
              i18nName: '回答',
            })
            const schemaWithName = InputKey(
              surveyQuestion.surveyQuestionId,
              (surveyQuestion.answerRequired ? formResolverUtils.required() : formResolverUtils).schema
            ).schemaWithName

            return {
              ...previousValue,
              ...schemaWithName,
            }
          }, {}),
        }),
      })
    ),
    defaultValues: {
      [surveyAnswersKeyName]: survey.surveyQuestions.reduce((previousValue, surveyQuestion) => {
        return {
          ...previousValue,
          [surveyQuestion.surveyQuestionId]: [],
        }
      }, {}),
    },
  })

  const handleSubmit = useCallback(async () => {
    const surveyAnswers = Object.keys(form.getValues(surveyAnswersKeyName)).reduce(
      (previousValue, surveyQuestionId) => {
        const surveyQuestion = survey.surveyQuestions.find(
          (surveyQuestion) => surveyQuestion.surveyQuestionId == surveyQuestionId
        )!
        return {
          ...previousValue,
          ...({
            [surveyQuestionId]: [
              ...(surveyQuestion.type == 'checkbox'
                ? form.getValues(surveyAnswersKeyName)[surveyQuestionId].map((surveyOptionId: string) => {
                    return { surveyOptionId }
                  })
                : []),
              ...(surveyQuestion.type == 'radio'
                ? [
                    {
                      surveyOptionId: form.getValues(surveyAnswersKeyName)[surveyQuestionId],
                    },
                  ]
                : []),
              ...(surveyQuestion.type == 'text'
                ? [
                    {
                      surveyOptionValue: form.getValues(`${surveyAnswersKeyName}.${surveyQuestionId}`),
                    },
                  ]
                : []),
            ],
          } as SurveyAnswer),
        }
      },
      {} as SurveyAnswer
    )

    // baseUserが残らない場合はローカルストレージに残す
    if (lineIdToken == null && liffId == null && loginUser == null) {
      setSurveyAnswer(survey.surveyId, surveyAnswers)
    }
    await answerSurvey({
      surveyId: survey.surveyId,
      surveyAnswers,
      lineIdToken,
      liffId,
    })

    const { success, loginSession } = await getLoginSession(true)
    if (success) {
      setLoginUser(loginSession.user)
    }

    afterSubmit()
  }, [])

  return (
    <>
      <FormProvider {...form}>
        <FormComponent form={form} handleSubmit={handleSubmit} survey={survey} simple={simple} />
      </FormProvider>
    </>
  )
}
